/**
 * Faz uma URL configurada seguir o host de onde a página foi MESMO aberta.
 *
 * O `.env` traz `localhost` porque é o que serve o dev na máquina anfitriã. Mas
 * o GreenVoice existe para uma empresa: os colegas abrem-no num IP de LAN
 * (`http://192.168.x.y`) ou num domínio — e aí `localhost` aponta para a máquina
 * DELES. A API, o gateway e o LiveKit ficam inalcançáveis e "nada funciona",
 * sendo que o código está todo certo: é só a URL a apontar para o sítio errado.
 *
 * Regra: se a URL configurada aponta para loopback MAS a página não foi aberta
 * em loopback, troca só o hostname pelo da página (mantém protocolo e porta).
 * Assim UM único build serve o `localhost` do dev E o IP de LAN da empresa, sem
 * editar o `.env` em cada máquina. Uma URL que já aponta para um host real
 * (domínio de produção) fica intacta.
 */

const LOOPBACK = new Set(["localhost", "127.0.0.1", "::1", "[::1]", "0.0.0.0"]);

/** Sem barra final: `${BASE}${path}` nunca pode produzir `//auth`. */
const semBarra = (s: string): string => s.replace(/\/+$/, "");

export function resolveHost(configured: string): string {
  if (typeof window === "undefined") return semBarra(configured);

  const page = window.location.hostname;
  // A própria página está em loopback (o dev na anfitriã): não há nada a trocar.
  if (LOOPBACK.has(page)) return semBarra(configured);

  try {
    const url = new URL(configured);
    // Já aponta para um host real (produção, domínio próprio): respeita-a.
    if (!LOOPBACK.has(url.hostname)) return semBarra(configured);
    url.hostname = page;
    return semBarra(url.toString());
  } catch {
    // Não era uma URL absoluta (ex.: caminho relativo same-origin): deixa como está.
    return semBarra(configured);
  }
}

/**
 * O mesmo para WebSockets — e com o caso que o `resolveHost` não sabe resolver:
 * um CAMINHO relativo.
 *
 * Trocar só o hostname (o que o `resolveHost` faz) chega para a LAN, onde a
 * porta continua a ser alcançável. Não chega para a PONTE: aí tudo entra por
 * uma única origem `https://…` e é o caminho que decide o serviço
 * (`/gateway` → API, `/livekit` → SFU). Um `ws://localhost:7880` nessa página
 * seria duas vezes impossível — o host é a máquina errada e um `ws://` numa
 * página `https://` é conteúdo misto, que o browser corta sem sequer tentar.
 *
 * Por isso um caminho relativo passa a ser configuração VÁLIDA: monta-se sobre
 * a origem da própria página, e o `wss` sai do `https` sozinho. Um `fetch` já
 * fazia isto de graça; o `WebSocket` exige URL absoluta e é preciso montá-la.
 */
export function resolveWs(configured: string): string {
  if (configured.startsWith("/")) {
    if (typeof window === "undefined") return configured;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${window.location.host}${semBarra(configured)}`;
  }
  return resolveHost(configured);
}

/**
 * O browser só expõe `getUserMedia`/`getDisplayMedia` num CONTEXTO SEGURO:
 * `https://` ou `http://localhost`. Num `http://<ip-de-lan>` a API de media nem
 * existe — daí a partilha de ecrã/câmara/micro falharem em toda a máquina que
 * não seja a anfitriã. Isto responde à pergunta: o pop-up de permissão é uma
 * fronteira de segurança do browser, não se contorna na web; mas sobre HTTPS o
 * browser GRAVA a autorização por origem (pop-up uma vez, nunca mais).
 */
export function isSecureMediaContext(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(window.isSecureContext && navigator.mediaDevices);
}
